import React, { useEffect, useState, useRef } from "react";
import { Image } from "@src/stories/elements/Image/Image";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "../../../../../entrypoints/theme";
import { useAlpineStore } from "@arctheme-hooks/useAlpineStore";
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import './ProductImages.scss';
import clsx from "clsx";

export const ProductImages = ({ product, isMobile, productUrl, isLazy = true, settings }) => {
  const store: any = useAlpineStore("modelview");
  let isModelView = false;
  const splideRef = useRef(null);
  const mobileSplideRef = useRef(null);

  if (store) {
    isModelView = store.isModelView;
  }
  const [modelView, setModelView] = useState(isModelView);
  const [isHovered, setIsHovered] = useState(false);

  const productImageWidth = 600;

  // Get block settings from window object or use defaults
  const blockSettings = settings;

  // Destructure all settings with defaults
  const {
    enable_hover_effect,
    hover_effect_type,
    mobile_effect_type,
    carousel_speed,
    carousel_start_index,
    carousel_loop,
    carousel_autoplay,
    carousel_autoplay_interval,
    carousel_show_arrows,
    carousel_show_pagination,
    carousel_draggable,
    icon_prev,
    icon_next,
    icon_size,
    icon_size_mobile,
    image_aspect_ratio
  } = settings || {};

  let mediaIndex = 0;
  let hoverIndex = 1;

  const getInitialStartIndex = () => {
    if (modelView) {
      if (product?.media?.length - 1 > 0) {
        return product?.media?.length - 1;
      } else {
        return 0;
      }
    } else {
      return carousel_start_index || 0;
    }
  };

  const initialStartIndex = getInitialStartIndex();

  if (modelView) {
    if (product?.media?.length - 1 > mediaIndex) {
      mediaIndex = product?.media?.length - 1;
      hoverIndex = 0;
    } else {
      mediaIndex = 0;
      hoverIndex = 0;
    }
  } else {
    hoverIndex = 1;
  }

  const getImageUrl = (media) => {
    if (typeof media === "object" && media !== null && media.src && media.media_type === "image") {
      return media?.src;
    }
    return media;
  };

  const getVideoUrl = (media) => {
    if (typeof media === "object" && media !== null && media.sources && media.media_type === "video") {
      if (!isMobile) {
        const desktopSource = media.sources.find(source => source.height > 700);
        return desktopSource?.url || media.sources[0]?.url;
      } else {
        const mobileSource = media.sources.find(source => source.height < 700);
        return mobileSource?.url || media.sources[0]?.url;
      }
    }
  };

  // Splide options for carousel
  const splideOptions = {
    type: carousel_loop ? 'loop' : 'slide',
    perPage: 1,
    pagination: carousel_show_pagination,
    autoplay: false, // Control autoplay manually
    interval: carousel_autoplay_interval,
    resetProgress: false,
    lazyLoad: "nearby" as "nearby",
    preloadPages: 1,
    gap: 0,
    heightRatio: 1,
    fixedHeight: '100%',
    start: initialStartIndex, // Use the calculated initial index
    speed: carousel_speed,
    arrows: false, // Disable built-in arrows so we can use custom ones
    drag: isMobile ? carousel_draggable : false, // Disable dragging on desktop
  };

  // Custom arrow handlers that properly reference the Splide instance
  const handlePrevSlide = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (splideRef.current && splideRef.current.splide) {
      splideRef.current.splide.go('<');
    }
  };

  const handleNextSlide = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (splideRef.current && splideRef.current.splide) {
      splideRef.current.splide.go('>');
    }
  };

  // Handle hover effects for carousel
  useEffect(() => {
    if (splideRef.current && splideRef.current.splide && hover_effect_type === 'carousel' && enable_hover_effect) {
      const splideInstance = splideRef.current.splide;
      
      if (isHovered && carousel_autoplay) {
        splideInstance.Components.Autoplay.play();
      } else {
        splideInstance.Components.Autoplay.pause();
        // Reset to initial start index when not hovering, not imageIndex
        if (!isHovered) {
          splideInstance.go(initialStartIndex);
        }
      }
    }
  }, [isHovered, hover_effect_type, carousel_autoplay, enable_hover_effect, initialStartIndex]);

  useEffect(() => {
    const handleModelViewToggle = (event) => {
      setModelView(event.detail.isModelView);
    };

    window.addEventListener("modelview-toggled", handleModelViewToggle);

    return () => {
      window.removeEventListener("modelview-toggled", handleModelViewToggle);
    };
  }, []);

  // Render carousel version (for non-hover carousel display)
  const renderCarousel = () => (
    <div className="w-full product-images-carousel relative">
      <Splide
        ref={mobileSplideRef}
        options={splideOptions}
        className="w-full h-full"
      >
        {product?.media?.map((media, index) => (
          <SplideSlide key={index}>
            {(media.media_type === 'image') ? (
              <Image
                imageUrl={getImageUrl(media)}
                width={productImageWidth}
                isLazy={isLazy}
                ratio={image_aspect_ratio}
              />
            ) : (
              <LazyVideo
                media={media}
                isActive={index === initialStartIndex}
                productImageWidth={productImageWidth}
              />
            )}
          </SplideSlide>
        ))}
        {carousel_show_arrows && (
          <>
            <button
              onClick={handlePrevSlide}
              className={`absolute left-2 top-1/2 transform -translate-y-1/2 z-10 icon-${icon_prev ? icon_prev : 'left-arrow'} text-${icon_size_mobile}`}
              aria-label="Previous Slide"
            >
            </button>
            <button
              onClick={handleNextSlide}
              className={`absolute right-2 top-1/2 transform -translate-y-1/2 z-10 icon-${icon_next ? icon_next : 'right-arrow'} text-${icon_size_mobile}`}
              aria-label="Next Slide"
            >
            </button>
          </>
        )}
      </Splide>
    </div>
  );

  // Lazy loading video component
  // Set CSS variable for media height coordination with promotional tiles
  const [mediaHeight, setMediaHeight] = useState<number | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateMediaHeight = () => {
      if (imageContainerRef.current) {
        const height = imageContainerRef.current.offsetHeight;
        if (height > 0) {
          setMediaHeight(height);
          document.documentElement.style.setProperty('--product-media-height', `${height}px`);
        }
      }
    };

    // Initial height measurement
    updateMediaHeight();

    // Update on window resize
    window.addEventListener('resize', updateMediaHeight);
    
    // Use ResizeObserver for more accurate detection
    let resizeObserver: ResizeObserver | null = null;
    if (imageContainerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateMediaHeight);
      resizeObserver.observe(imageContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateMediaHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [product?.media, isMobile, modelView]);

  const LazyVideo = ({ media, isActive, productImageWidth }) => {
    const [shouldLoad, setShouldLoad] = useState(isActive);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const videoRef = useRef(null);

    // Intersection Observer for lazy loading
    useEffect(() => {
      if (!videoRef.current) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsIntersecting(entry.isIntersecting);
        },
        { threshold: 0.1 }
      );

      observer.observe(videoRef.current);

      return () => observer.disconnect();
    }, []);

    // Load video when it becomes active or intersecting
    useEffect(() => {
      if (isActive || isIntersecting) {
        setShouldLoad(true);
      }
    }, [isActive, isIntersecting]);

    return (
      <div ref={videoRef} className="w-full h-full">
        {shouldLoad ? (
          <video
            autoPlay={isActive}
            muted={true}
            loop={true}
            controls={false}
            playsInline={true}
            key={media.sources[0].url}
            className="w-full h-full object-cover"
          >
            <source src={media.sources[0].url} type={'video/mp4'}/>
            Your browser does not support the video tag.
          </video>
        ) : (
          // Show placeholder or first frame while not loaded
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <div className="text-gray-500">Loading video...</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {!isMobile ? (
        <a
          href={`${productUrl}`}
          title={product?.title || 'Product'}
          className="w-full product-images"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="relative w-full" ref={imageContainerRef}>
            <div className={`w-full h-full`}>
              {product?.media?.[mediaIndex] ? (
                <Image
                  imageUrl={getImageUrl(product.media[mediaIndex])}
                  width={productImageWidth}
                  ratio={image_aspect_ratio}
                  classes={clsx([{
                    'opacity-0': enable_hover_effect && product?.media?.length > 1 && hover_effect_type !== 'carousel' && isHovered,
                  }])}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <div className="text-gray-500">No image available</div>
                </div>
              )}
            </div>
            
            {/* Hover overlay - secondary image or carousel */}
            {enable_hover_effect && (
              <div className={`absolute inset-0 w-full h-full ${
                isHovered ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              }`}>
                {hover_effect_type === 'carousel' && product?.media?.length > 1 ? (
                  <div className="w-full h-full product-images-carousel relative">
                    <Splide
                      ref={splideRef}
                      options={splideOptions}
                      className="w-full h-full overflow-hidden"
                    >
                      {product?.media?.map((media, index) => (
                        <SplideSlide key={index}>
                          {media.media_type === 'image' ? (
                            <Image
                              imageUrl={getImageUrl(media)}
                              width={productImageWidth}
                              isLazy={isLazy}
                              ratio={image_aspect_ratio}
                            />
                          ) : (
                            <LazyVideo
                              media={media}
                              isActive={isHovered && index === initialStartIndex}
                              productImageWidth={productImageWidth}
                            />
                          )}
                        </SplideSlide>
                      ))}
                    </Splide>

                    {carousel_show_arrows && (
                      <>
                        <button
                          onClick={handlePrevSlide}
                          className={`absolute left-2 top-1/2 transform -translate-y-1/2 z-10 icon-${icon_prev ? icon_prev : 'left-arrow'} text-${icon_size}`}
                          aria-label="Previous Slide"
                        >
                        </button>
                        <button
                          onClick={handleNextSlide}
                          className={`absolute right-2 top-1/2 transform -translate-y-1/2 z-10 icon-${icon_next ? icon_next : 'right-arrow'} text-${icon_size}`}
                          aria-label="Next Slide"
                        >
                        </button>
                      </>
                    )}
                  </div>
                ) : hover_effect_type !== 'carousel' ? (
                  // Standard hover image switching
                  product?.media?.[hoverIndex] && (
                    <>
                      {product.media[hoverIndex].media_type === 'image' ? (
                        <Image
                          imageUrl={getImageUrl(product.media[hoverIndex])}
                          width={productImageWidth}
                          isLazy={isLazy}
                          ratio={image_aspect_ratio}
                        />
                      ) : (
                        <LazyVideo
                          media={product.media[hoverIndex]}
                          isActive={isHovered}
                          productImageWidth={productImageWidth}
                        />
                      )}
                    </>
                  )
                ) : null}
              </div>
            )}
          </div>
        </a>
      ) : (
        <a
          href={`${productUrl}`}
          className="w-full product-images"
        >
          {mobile_effect_type === 'carousel' && product?.media?.length > 1 ? 
            renderCarousel() : 
            (product?.media?.[mediaIndex] ? (
              <Image
                imageUrl={getImageUrl(product.media[mediaIndex])}
                width={productImageWidth}
                ratio={image_aspect_ratio}
              />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                <div className="text-gray-500">No image available</div>
              </div>
            ))
          }
        </a>
      )}
    </>
  );
};